import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import FullscreenExitSharpIcon from '@mui/icons-material/FullscreenExitSharp';
import FullscreenSharpIcon from '@mui/icons-material/FullscreenSharp';
import CloseIcon from '@mui/icons-material/Close';
import { ExpandableContentWrapper, Tabs } from 'components/shared';
import {
  formatMaxProfit,
  formatMaxRisk,
  getCarryColor,
  getEMA,
  getVRPColor,
  netCash,
  num,
  pct,
  pct0,
  rewardToRiskRatio,
  stockLegToShares,
  tierColor,
  tierFromRiskScore,
  TIER_LABEL,
  usd,
} from 'util/moviefoneFormat';
import MoviefoneOverviewSummary from './MoviefoneOverviewSummary';
import {
  CorrelationMetrics,
  DollarValuePerContract,
  ForwardVolPair,
  Leg,
  MoviefoneAnalysis,
  ProjectedSurface,
  SkewPlotResponse,
  SearchCandidate,
  VolatilitySnapshot,
} from 'gen/stream-api/requests';
import {
  KVTable,
  LegsSummary,
  MiniTable,
  TickerQuote,
} from './MoviefoneShared';
import useReadableTextColors from 'hooks/moviefone/useReadableTextColors';
import VolatilityChartPanel from './VolatilityChartPanel';
import { VolatilitySurface3D as VolatilitySurface3DType } from 'types/moviefone';
import {
  dayjs,
  ET,
  NYSE_CLOSING_ET_HOUR,
  stockMarketClose,
} from 'util/shared/date';
import { Dayjs } from 'dayjs';
import { shortIdGenerator } from 'util/optionsCalculator';
import {
  OptionsCalculatorCanvasConfig,
  OptionType as OCOptionType,
  PositionLeg,
} from 'types/optionsCalculator';
import { CanvasOptionsCalculatorProvider } from 'hooks/optionsCalculator/CanvasOptionsCalculatorProvider';
import OptionsCalculatorContainer from 'pages/components/OptionsCalculatorContainer';
import { VolSkewProvider } from 'components/implied_vol/VolSkew';
import VolSkewChart from 'components/implied_vol/VolSkew/VolSkewChart';
import { TermStructureProvider } from 'components/implied_vol/TermStructure';
import TermStructureChart from 'components/implied_vol/TermStructure/TermStructureChart';
import { Controls } from 'components/shared/Controls';
import {
  DatedGreeks,
  DEFAULT_TERM_STRUCTURE_CONFIG,
  DEFAULT_VOL_SKEW_CONFIG,
  Earnings,
  ETFSymbols,
  ExpirationsDisplayType,
  GreeksStatsLookback,
  MacroCalendarEvent,
  RawGreeksDataMap,
  TermStructure,
  TermStructureConfig,
  VolSkew,
  VolSkewConfig,
} from 'types';
import { getQueryDate } from 'util/shared';
import {
  DELTA_KEY,
  convertToDaysTillExpiry,
  findClosestExpiration,
  getSkewDelta,
  getSnapshotTime,
  getVolSkewTimeframeString,
} from 'util/iVol';
import { applyMovingAverage, interpolateDataRow } from 'util/riskEngine';
import useImpliedVolatility from 'hooks/iVol/useImpliedVolatility';
import { hasInternalDashAccessState } from 'states';
import useMacroCalendar from 'hooks/home/useMacroCalendar';
import useHomeContent from 'hooks/home/useHomeContent';

const GEN_OPTION_TYPE_TO_OC: Record<'Call' | 'Put', OCOptionType> = {
  Call: OCOptionType.CALL,
  Put: OCOptionType.PUT,
};

// Taller than the 500px default - the embedded calculator was too cramped at that size.
const OPTIONS_CALCULATOR_CHART_HEIGHT = 375;

// SearchCandidate legs come from the moviefone scanner (ISO expiry, total price/greeks
// for `quantity` contracts); the options calculator's PositionLeg expects
// epoch-millis expiry and per-contract price, with buy/sell encoded as the
// sign of quantity.
const legToPositionLeg = (leg: Leg, sym: string): PositionLeg | null => {
  if (leg.option_type === 'Stock') {
    return null;
  }
  const quantity = leg.quantity ?? 1;
  return {
    leg_id: shortIdGenerator(),
    sym,
    strike: leg.strike,
    // 4pm ET (market close), not midnight - matches real market expiry
    // timestamps, confirmed live against the standalone calculator's own.
    expiry: dayjs
      .tz(leg.expiry, ET)
      .hour(NYSE_CLOSING_ET_HOUR)
      .minute(0)
      .second(0)
      .millisecond(0)
      .valueOf(),
    option_type: GEN_OPTION_TYPE_TO_OC[leg.option_type],
    quantity: leg.action === 'Sell' ? -quantity : quantity,
    price_paid: quantity !== 0 ? leg.price / quantity : leg.price,
  };
};

// The embedded calculator reads its legs once on mount, so remount it per trade.
const legsKey = (legs: Leg[]): string =>
  legs
    .map(
      (l) =>
        `${l.action} ${l.quantity} ${l.option_type} ${l.strike} ${l.expiry}`,
    )
    .join('|');

export interface StrategyOptionsCalculatorProps {
  searchCandidate: SearchCandidate;
  symbol: string;
  hidePnlChart?: boolean;
  hideHeatmap?: boolean;
  simplifedPositions?: boolean;
  chartHeight?: number;
  positionsBottomMargin?: string | number;
  sectionGap?: number;
  defaultPositionsCollapsed?: boolean;
}

// Wraps the full standalone Options Calculator, pre-seeded with this
// SearchCandidate's legs, using the same canvas-widget provider path (local
// config state instead of a real canvas) so no new state plumbing is needed.
export const StrategyOptionsCalculator = ({
  searchCandidate,
  symbol,
  hidePnlChart = false,
  hideHeatmap = false,
  simplifedPositions = true,
  chartHeight,
  positionsBottomMargin,
  sectionGap,
  defaultPositionsCollapsed,
}: StrategyOptionsCalculatorProps) => {
  const [calcSym, setCalcSym] = useState(symbol);
  const [calcConfig, setCalcConfig] = useState<OptionsCalculatorCanvasConfig>(
    () => {
      const legs = searchCandidate.legs
        .map((leg) => legToPositionLeg(leg, symbol))
        .filter((leg): leg is PositionLeg => leg != null);
      return {
        sym: symbol,
        legs,
        shares: searchCandidate.legs
          .filter((leg) => leg.option_type === 'Stock')
          .map((leg) => stockLegToShares(leg, symbol)),
        ivSettings: [],
        // Default the slider to the position's own expiration, same as the
        // standalone calculator does for its auto-generated default leg.
        baseDate:
          legs.length > 0
            ? Math.min(...legs.map((leg) => leg.expiry))
            : undefined,
      };
    },
  );

  return (
    <CanvasOptionsCalculatorProvider
      sym={calcSym}
      onSymChange={setCalcSym}
      config={calcConfig}
      onConfigChange={(patch) =>
        setCalcConfig((prev) => ({ ...prev, ...patch }))
      }
    >
      <OptionsCalculatorContainer
        enableGlobalSymbolSearch={false}
        hideHeader={true}
        hidePnlChart={hidePnlChart}
        hideHeatmap={hideHeatmap}
        simplifedPositions={simplifedPositions}
        chartHeight={chartHeight}
        positionsBottomMargin={positionsBottomMargin}
        sectionGap={sectionGap}
        defaultPositionsCollapsed={defaultPositionsCollapsed}
      />
    </CanvasOptionsCalculatorProvider>
  );
};

interface StrategyTermStructureChartProps {
  searchCandidate: SearchCandidate;
  symbol: string;
  volatility: VolatilitySnapshot;
  projectedSurface: ProjectedSurface | null | undefined;
}

// Monthly options expiration: the 3rd Friday of a given date's month.
const getThirdFridayOfMonth = (d: Dayjs): Dayjs => {
  const firstOfMonth = d.startOf('month');
  const firstFridayOffset = (5 - firstOfMonth.day() + 7) % 7;
  return firstOfMonth.add(firstFridayOffset + 14, 'day');
};

// The next monthly OPEX on or after `date`.
const getNextMonthlyOpex = (date: Dayjs): Dayjs => {
  const thisMonth = getThirdFridayOfMonth(date);
  return thisMonth.isBefore(date, 'day')
    ? getThirdFridayOfMonth(date.add(1, 'month'))
    : thisMonth;
};

const getLegExpiries = (searchCandidate: SearchCandidate): Dayjs[] =>
  searchCandidate.legs
    .filter((leg) => leg.option_type !== 'Stock')
    .map((leg) => dayjs(leg.expiry));

const getFurthestExpiry = (searchCandidate: SearchCandidate): Dayjs | null => {
  const expiries = getLegExpiries(searchCandidate);
  return expiries.length === 0
    ? null
    : expiries.reduce((a, b) => (a.isAfter(b) ? a : b));
};

const getEarliestExpiry = (searchCandidate: SearchCandidate): Dayjs | null => {
  const expiries = getLegExpiries(searchCandidate);
  return expiries.length === 0
    ? null
    : expiries.reduce((a, b) => (a.isBefore(b) ? a : b));
};

// The lookback stats are bucketed by market-data deltas, so the band is looked up by them too.
const getMarketSkewDeltas = (
  greeks: RawGreeksDataMap,
  targetDte: number,
  strikes: number[],
): number[] | null => {
  const expirations = Object.keys(greeks);
  if (expirations.length === 0) {
    return null;
  }
  const chain =
    greeks[
      Number(
        findClosestExpiration(getSnapshotTime(greeks), expirations, targetDte),
      )
    ];
  const deltaByStrike = Object.entries(chain)
    .map(([strike, callPut]) => ({
      strike: Number(strike),
      value: getSkewDelta(callPut),
    }))
    .sort((a, b) => a.strike - b.strike);
  return strikes.map(
    (strike) => interpolateDataRow(deltaByStrike, [strike])[0],
  );
};

// Trades <=30 DTE get +10 days past their furthest expiration; longer ones
// extend to the next monthly OPEX - but always at least 2 weeks past the
// furthest expiration, since OPEX can land within days of it.
const getDefaultEndDte = (searchCandidate: SearchCandidate): number | null => {
  const furthest = getFurthestExpiry(searchCandidate);
  if (furthest == null) {
    return null;
  }
  const today = getQueryDate(true).startOf('day');
  const furthestDte = furthest.diff(today, 'day');
  const computedEndDate =
    furthestDte <= 30 ? furthest.add(10, 'day') : getNextMonthlyOpex(furthest);
  const minEndDate = furthest.add(14, 'day');
  const endDate = computedEndDate.isAfter(minEndDate)
    ? computedEndDate
    : minEndDate;
  return endDate.diff(today, 'day');
};

// Long calls and short puts profit from the underlying rising; short calls
// and long puts profit from it falling.
const isBullishLeg = (leg: Leg): boolean =>
  (leg.action === 'Buy' && leg.option_type === 'Call') ||
  (leg.action === 'Sell' && leg.option_type === 'Put');

// Event names can already end in their own parenthetical (the reporting period) - strip it to avoid "(...) (...)".
const stripTrailingParenthetical = (name: string): string =>
  name.replace(/\s*\([^)]*\)\s*$/, '');

const StatsLookbackPills = ({
  value,
  onChange,
}: {
  value: GreeksStatsLookback;
  onChange: (lookback: GreeksStatsLookback) => void;
}) => (
  <Stack sx={{ gap: 0.5, alignItems: 'flex-start' }}>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      Historical IV Range
    </Typography>
    <Controls
      buttonGroups={[
        {
          buttons: Object.values(GreeksStatsLookback).map((lookback) => ({
            value: lookback,
            label: `${lookback} days`,
          })),
          setter: onChange,
          curValue: value,
        },
      ]}
    />
  </Stack>
);

// Wraps the real TermStructureChart with this card's own precomputed data - no controls/brush.
const StrategyTermStructureChart = ({
  searchCandidate,
  symbol,
  volatility,
  projectedSurface,
}: StrategyTermStructureChartProps) => {
  const theme = useTheme();
  const defaultEndDte = getDefaultEndDte(searchCandidate);
  const withinDefaultRange = (dte: number) =>
    defaultEndDte == null || dte <= defaultEndDte;

  const [config, setConfig] = useState<TermStructureConfig>(() => {
    const projectedExpirations = (
      projectedSurface?.projected_expirations ?? []
    ).filter((exp) => withinDefaultRange(exp.dte));
    // Arbitrary future date, just needs to differ from today's key - legendLabel overrides the display text.
    const projectedTradeDate = getQueryDate(true).add(1, 'day');
    const projectedKey = projectedTradeDate.format('YYYY-MM-DD');

    return {
      ...DEFAULT_TERM_STRUCTURE_CONFIG,
      displayType: ExpirationsDisplayType.DaysToExpiration,
      showZoomBrush: false,
      showEconomicEvents: false,
      showDividends: false,
      interpolateTooltipValues: true,
      markedExpirations: searchCandidate.legs
        .filter((leg) => leg.option_type !== 'Stock')
        .map((leg) => ({
          date: dayjs(leg.expiry).valueOf(),
          label: dayjs(leg.expiry).format('M/D'),
          color: isBullishLeg(leg)
            ? theme.palette.success.main
            : theme.palette.error.main,
        })),
      additionalDates:
        projectedExpirations.length === 0
          ? []
          : [
              {
                key: projectedKey,
                tradeDate: projectedTradeDate,
                color: '',
                currentPrice: 0,
                legendLabel: 'Projected',
                precomputedTermStructure: projectedExpirations.map(
                  (exp): TermStructure => ({
                    daysToExpiry: exp.dte,
                    expirationDate: getQueryDate(true)
                      .add(exp.dte, 'day')
                      .valueOf(),
                    [projectedKey]: exp.atm_iv,
                  }),
                ),
              },
            ],
    };
  });

  const todayKey = getQueryDate(true).format('YYYY-MM-DD');
  const precomputedToday: TermStructure[] = volatility.expirations
    .filter((exp) => withinDefaultRange(exp.dte))
    .map((exp) => ({
      daysToExpiry: exp.dte,
      expirationDate: stockMarketClose(dayjs.tz(exp.date, ET)).valueOf(),
      [todayKey]: exp.atm_iv,
    }));

  return (
    <Stack sx={{ gap: 1 }}>
      <StatsLookbackPills
        value={config.statsLookback}
        onChange={(statsLookback) =>
          setConfig((prev) => ({ ...prev, statsLookback }))
        }
      />
      <Box sx={{ height: 240 }}>
        <TermStructureProvider
          selectedSym={symbol}
          config={config}
          onConfigChange={(patch) =>
            setConfig((prev) => ({ ...prev, ...patch }))
          }
          precomputedTodayTermStructure={precomputedToday}
          todayLegendLabel="Current"
        >
          <TermStructureChart />
        </TermStructureProvider>
      </Box>
    </Stack>
  );
};

interface StrategyVolSkewChartProps {
  searchCandidate: SearchCandidate;
  symbol: string;
  spot: number;
  skewData: SkewPlotResponse;
  dollarValueContracts: DollarValuePerContract[];
}

// Wraps the real Vol Dashboard VolSkewChart with this card's own precomputed
// data (see DatedGreeks.precomputedVolSkew) so it never independently
// re-derives IV. Shows the skew for the trade's earliest expiration alongside
// the model's Projected IV for that same expiration, with the trade's own
// strikes marked.
const StrategyVolSkewChart = ({
  searchCandidate,
  symbol,
  spot,
  skewData,
  dollarValueContracts,
}: StrategyVolSkewChartProps) => {
  const theme = useTheme();
  const { getCurrentGreeksData } = useImpliedVolatility();
  const [marketGreeks, setMarketGreeks] = useState<RawGreeksDataMap | null>(
    null,
  );
  useEffect(() => {
    let cancelled = false;
    void getCurrentGreeksData(symbol).then((greeks) => {
      if (!cancelled) {
        setMarketGreeks(greeks);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [symbol, getCurrentGreeksData]);
  const [config, setConfig] = useState<VolSkewConfig>(() => ({
    ...DEFAULT_VOL_SKEW_CONFIG,
    showZoomBrush: false,
    showDividends: false,
    showSkewDiff: false,
    markedStrikes: searchCandidate.legs
      .filter((leg) => leg.option_type !== 'Stock')
      .map((leg) => ({
        strike: leg.strike,
        // Expiry date - PM feedback: without it, a mixed-expiration
        // searchCandidate's marked strikes are ambiguous about which expiration
        // each one belongs to.
        label: `${leg.action} ${leg.strike}${leg.option_type[0].toUpperCase()} - ${dayjs(leg.expiry).format('M/D')}`,
        // Not sgGreen - PM feedback: too close to the chart's own teal
        // "Current Price" marker. success/error are a more standard,
        // visually distinct red/green pair for bullish/bearish.
        color: isBullishLeg(leg)
          ? theme.palette.success.main
          : theme.palette.error.main,
      })),
  }));

  const today = getQueryDate(true);
  const precomputedLines: DatedGreeks[] = [];

  // The trade's earliest expiration (nearest to today if it has no option legs).
  const earliestExpiry = getEarliestExpiry(searchCandidate);
  const targetDte =
    earliestExpiry == null
      ? 0
      : earliestExpiry.diff(today.startOf('day'), 'day');
  const currentExp =
    skewData.expirations.length === 0
      ? null
      : skewData.expirations.reduce((a, b) =>
          Math.abs(b.dte - targetDte) < Math.abs(a.dte - targetDte) ? b : a,
        );
  const expirationText =
    currentExp == null
      ? ''
      : `${dayjs(currentExp.expiration).format('M/D')}, ${convertToDaysTillExpiry(
          today.valueOf(),
          stockMarketClose(dayjs.tz(currentExp.expiration, ET)).valueOf(),
        )} DTE`;
  if (currentExp != null) {
    const smoothedIVs = getEMA(currentExp.ivs);
    // VolSkewChart recomputes each line's row-data key as
    // `getVolSkewTimeframeString(tradeDate, expiryDate)` rather than reusing
    // this entry's own `key` - see DatedGreeks.precomputedVolSkew.
    const currentKey = getVolSkewTimeframeString(today, currentExp.expiration);
    const marketDeltas =
      marketGreeks == null
        ? null
        : getMarketSkewDeltas(marketGreeks, currentExp.dte, currentExp.strikes);
    precomputedLines.push({
      key: currentKey,
      sym: symbol,
      tradeDate: today,
      expiryDate: currentExp.expiration,
      greeksMap: {},
      currentPrice: spot,
      statsMap: {},
      color: '',
      legendLabel: `Current (${expirationText})`,
      precomputedVolSkew: currentExp.strikes.map(
        (strike, i): VolSkew => ({
          strike,
          moneyness: spot > 0 ? strike / spot : 1,
          // Never actually shown - Moviefone doesn't expose the delta
          // display mode - just needs to clear the chart's internal sanity
          // filter (rows with delta-x outside (0, 1) get dropped).
          'delta-x': 0.5,
          [currentKey]: smoothedIVs[i],
          ...(marketDeltas == null
            ? {}
            : { [`${currentKey}-${DELTA_KEY}`]: marketDeltas[i] }),
        }),
      ),
    });
  }

  // Projected IV, from the volatility model (confirmed with backend: same
  // `projected_iv` the dollar-value scanner uses). Matched to "Current"'s
  // expiration for comparability, using each strike's OTM contract (puts
  // below spot, calls at/above) - the standard skew convention.
  //
  // Matched by closest `dte`, not exact `expiration` string equality:
  // skewData's target-DTE buckets can be interpolated/synthetic dates that
  // don't match any real expiration in dollarValueContracts.
  const closestRealExpiration =
    currentExp == null || dollarValueContracts.length === 0
      ? null
      : dollarValueContracts.reduce((closest, c) =>
          Math.abs(c.dte - currentExp.dte) <
          Math.abs(closest.dte - currentExp.dte)
            ? c
            : closest,
        ).expiration;
  const projectedContracts =
    closestRealExpiration == null
      ? []
      : dollarValueContracts.filter(
          (c) =>
            c.expiration === closestRealExpiration &&
            (spot > 0 && c.strike < spot ? !c.is_call : c.is_call),
        );
  if (projectedContracts.length > 0) {
    // Raw per-contract IVs, unlike skewData's already-fitted line - smoothed
    // with a *centered* moving average (same technique riskEngine.ts uses
    // for vol surfaces), not EMA: EMA's decay lets one bad point near the
    // start drag down every point after it; a centered window doesn't.
    const sortedProjected = projectedContracts
      .slice()
      .sort((a, b) => a.strike - b.strike);
    const smoothedProjectedIVs = applyMovingAverage(
      sortedProjected.map((c) => c.projected_iv),
      3,
    );
    // Arbitrary date distinct from `today` above - only needs to produce a
    // different line key; legendLabel below overrides the displayed text.
    const projectedTradeDate = today.add(1, 'day');
    const projectedKey = getVolSkewTimeframeString(
      projectedTradeDate,
      currentExp!.expiration,
    );
    precomputedLines.push({
      key: projectedKey,
      sym: symbol,
      tradeDate: projectedTradeDate,
      expiryDate: currentExp!.expiration,
      greeksMap: {},
      currentPrice: spot,
      statsMap: {},
      color: '',
      legendLabel: `Projected IV (${expirationText})`,
      // Drops the first strike/IV point - still an unsmoothed passthrough
      // (window size 1) at the very edge.
      precomputedVolSkew: sortedProjected
        .slice(1)
        .map(
          (c, i): VolSkew => ({
            strike: c.strike,
            moneyness: spot > 0 ? c.strike / spot : 1,
            // Never actually shown - Moviefone doesn't expose the delta
            // display mode - just needs to clear the chart's internal
            // sanity filter (rows with delta-x outside (0, 1) get dropped).
            'delta-x': 0.5,
            [projectedKey]: smoothedProjectedIVs[i + 1],
          }),
        )
        .filter(
          (row) =>
            row.strike >= Math.min(...currentExp!.strikes) &&
            row.strike <= Math.max(...currentExp!.strikes),
        ),
    });
  }

  return (
    <Stack sx={{ gap: 1 }}>
      <StatsLookbackPills
        value={config.statsLookback}
        onChange={(statsLookback) =>
          setConfig((prev) => ({ ...prev, statsLookback }))
        }
      />
      <Box sx={{ height: 400 }}>
        <VolSkewProvider
          selectedSym={symbol}
          config={config}
          onConfigChange={(patch) =>
            setConfig((prev) => ({ ...prev, ...patch }))
          }
        >
          <VolSkewChart precomputedLines={precomputedLines} />
        </VolSkewProvider>
      </Box>
    </Stack>
  );
};

interface MoviefoneStrategyCardProps {
  searchCandidate: SearchCandidate;
  data: MoviefoneAnalysis;
  onAsk?: (msg: string) => void;
  onClose?: () => void;
}

const MoviefoneStrategyCard = ({
  searchCandidate: s,
  data,
  onAsk,
  onClose,
}: MoviefoneStrategyCardProps) => {
  const {
    symbol,
    spot,
    volatility,
    tvtp_regime: tvtpRegime,
    forecast,
    regime,
    enriched_regime,
    correlation_regime,
    spot_vol_regime,
    vol_forecast,
    synth_oi,
    scenario_forecast,
    vrp_term_structure,
    projected_surface,
    surface_residuals,
    skew_plot_data,
    vol_surface_3d,
    dollar_value_contracts,
  } = data ?? {};
  const theme = useTheme();
  const textColors = useReadableTextColors();
  const isInternalUser = useRecoilValue(hasInternalDashAccessState);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [tab, setTab] = useState('overview');
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  // null = no event risk found (or still checking) - the badge only shows when there's something to flag.
  const [eventRisk, setEventRisk] = useState<{
    name: string;
    date: string;
  } | null>(null);
  const { getMacroCalendar } = useMacroCalendar();
  const { getEarningsForSyms } = useHomeContent();
  const furthestExpiry = useMemo(() => getFurthestExpiry(s), [s]);

  useEffect(() => {
    if (furthestExpiry == null) {
      setEventRisk(null);
      return;
    }
    const checkEventRisk = async () => {
      try {
        const today = getQueryDate(true);
        if (ETFSymbols.has(symbol)) {
          const events = (await getMacroCalendar(
            today.format('YYYY-MM-DD'),
            furthestExpiry.format('YYYY-MM-DD'),
          )) as MacroCalendarEvent[];
          const highImpact = events.find(
            (event) => event.impact === 'High' && event.country === 'US',
          );
          setEventRisk(
            highImpact != null
              ? {
                  name: stripTrailingParenthetical(highImpact.event),
                  date: dayjs(highImpact.date).format('M/D'),
                }
              : null,
          );
        } else {
          const events: Earnings[] = await getEarningsForSyms(
            [symbol],
            today,
            furthestExpiry,
          );
          setEventRisk(
            events.length > 0
              ? { name: 'Earnings', date: dayjs(events[0].day).format('M/D') }
              : null,
          );
        }
      } catch (err) {
        console.error('Error checking event risk:', err);
        setEventRisk(null);
      }
    };
    void checkEventRisk();
  }, [symbol, furthestExpiry, getMacroCalendar, getEarningsForSyms]);

  // Truncate just the name, never the date - event names can run long, the date can't.
  const MAX_EVENT_NAME_LENGTH = 22;
  const eventRiskDisplayLabel =
    eventRisk != null
      ? `${
          eventRisk.name.length > MAX_EVENT_NAME_LENGTH
            ? `${eventRisk.name.slice(0, MAX_EVENT_NAME_LENGTH - 1)}…`
            : eventRisk.name
        } (${eventRisk.date})`
      : null;

  const tier =
    s.risk_score != null ? tierFromRiskScore(s.risk_score.total) : null;
  const badgeColor = tierColor(tier, theme);
  const isFixedRisk =
    Number.isFinite(s.max_risk) && Number.isFinite(s.max_profit);
  const rewardRiskRatio = rewardToRiskRatio(s);
  const rewardRisk =
    rewardRiskRatio != null ? rewardRiskRatio.toFixed(2) : 'N/A';
  const cash = netCash(s);

  const hasNotes =
    s.carry != null ||
    s.theta_income != null ||
    (s.oi_annotation != null &&
      ((s.oi_annotation.warning != null && s.oi_annotation.warning !== '') ||
        (s.oi_annotation.confirming != null &&
          s.oi_annotation.confirming !== '')));

  return (
    <ExpandableContentWrapper isOpen={isFullScreen} onToggle={setIsFullScreen}>
      <Card
        sx={{
          borderRadius: '10px',
          overflow: 'hidden',
          boxShadow: 'none',
          backgroundColor: theme.palette.background.paper,
          height: '100%',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent
          sx={{
            p: '16px',
            '&:last-child': { pb: '16px' },
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.5px',
                color: badgeColor,
              }}
            >
              {tier != null ? TIER_LABEL[tier] : 'STRATEGY'}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {s.final_score != null && s.final_score > 0 && (
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: theme.palette.text.secondary }}
                >
                  Score {s.final_score.toFixed(2)}
                </Typography>
              )}
              <IconButton
                size="small"
                onClick={() => setIsFullScreen((prev) => !prev)}
                aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'}
                sx={{
                  p: '2px',
                  color: theme.palette.sgGreen,
                  '&:hover': { color: theme.palette.text.primary },
                }}
              >
                {isFullScreen ? (
                  <FullscreenExitSharpIcon fontSize="small" />
                ) : (
                  <FullscreenSharpIcon fontSize="small" />
                )}
              </IconButton>
              {onClose != null && (
                <Tooltip title="Close" placement="bottom">
                  <IconButton
                    size="small"
                    onClick={onClose}
                    aria-label="Close trade"
                    sx={{
                      p: '2px',
                      cursor: 'pointer',
                      color: theme.palette.sgGreen,
                      '&:hover': { color: theme.palette.text.primary },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ flexWrap: 'wrap', columnGap: 2, mb: 1.5 }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ flexWrap: 'wrap' }}
            >
              <Typography
                variant="h6"
                onClick={() =>
                  onAsk?.(
                    `Explain this strategy in detail: ${s.family_label}. Legs: ${s.legs.map((l) => `${l.action} ${l.strike}${l.option_type[0].toUpperCase()}`).join(', ')}. Score: ${s.final_score?.toFixed(2) ?? 'N/A'}, Risk: ${s.risk_score?.total.toFixed(0) ?? 'N/A'}/100, POP: ${s.pop != null ? Math.round(s.pop) + '%' : 'N/A'}, EV: ${usd(s.expected_return)}, R/R: ${rewardRisk}. Rationale: ${s.rationale}`,
                  )
                }
                sx={{
                  fontWeight: 700,
                  mt: 0.5,
                  mb: 0.5,
                  cursor: onAsk != null ? 'pointer' : 'default',
                }}
              >
                <TickerQuote
                  symbol={symbol}
                  spot={spot}
                  tradeDate={data.trade_date}
                />{' '}
                {s.family_label}
              </Typography>
              {eventRiskDisplayLabel != null && (
                <Chip
                  title={
                    eventRisk != null
                      ? `${eventRisk.name} (${eventRisk.date})`
                      : undefined
                  }
                  label={`Event Risk: ${eventRiskDisplayLabel}`}
                  size="small"
                  sx={{
                    height: '20px',
                    backgroundColor: theme.palette.error.main,
                    color: theme.palette.error.contrastText,
                    border: `1px solid ${theme.palette.error.dark}`,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '10px',
                    '& .MuiChip-label': { px: '6px' },
                  }}
                />
              )}
            </Stack>
            <Stack
              direction="row"
              alignItems="baseline"
              sx={{ flexWrap: 'wrap', gap: '12px' }}
            >
              <LegsSummary legs={s.legs} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  whiteSpace: 'nowrap',
                }}
              >
                {cash.label} {cash.value}
              </Typography>
            </Stack>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '8px',
              py: '12px',
              borderTop: `1px dashed ${theme.palette.divider}`,
              borderBottom: `1px dashed ${theme.palette.divider}`,
            }}
          >
            {[
              {
                label: 'Max Profit',
                value: formatMaxProfit(s),
                color: textColors.success,
              },
              {
                label: 'Max Risk',
                value: formatMaxRisk(s),
                color: textColors.error,
              },
              {
                label: 'Breakeven',
                value:
                  // Older saved conversations predate this field.
                  (s.breakevens ?? []).length > 0
                    ? s.breakevens.map(usd).join(' / ')
                    : 'N/A',
              },
              {
                label: 'POP',
                value: s.pop != null ? `${Math.round(s.pop)}%` : 'N/A',
              },
              { label: 'Reward / Risk', value: rewardRisk },
            ].map((stat) => (
              <Box key={stat.label} sx={{ textAlign: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontSize: '9.5px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  {stat.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: stat.color ?? theme.palette.text.primary,
                  }}
                >
                  {stat.value}
                </Typography>
              </Box>
            ))}
          </Box>

          <TabContext value={tab}>
            <Box
              sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mt: 1 }}
            >
              <Tabs
                options={
                  new Map([
                    ['overview', 'Overview'],
                    ['volatility', 'Volatility'],
                  ])
                }
                onChange={handleTabChange}
                fontSize="12.5px"
                tabListSx={{ minHeight: 0 }}
                tabButtonSx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  fontSize: '12.5px',
                  padding: '8px 14px',
                  minHeight: 0,
                  '&:hover': { color: theme.palette.text.primary },
                  '&.Mui-selected': {
                    color: textColors.sgGreen,
                    fontWeight: 600,
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                pt: '14px',
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <TabPanel value="overview" sx={{ p: 0 }}>
                  <Stack sx={{ gap: '16px' }}>
                    <MoviefoneOverviewSummary
                      searchCandidate={s}
                      dollarValueContracts={dollar_value_contracts ?? []}
                      forecast={forecast}
                      tvtp={tvtpRegime}
                      showInternalOnly={isInternalUser}
                      enrichedRegime={enriched_regime}
                      regime={regime}
                      vrpTermStructure={vrp_term_structure}
                    />

                    <StrategyOptionsCalculator
                      key={legsKey(s.legs)}
                      searchCandidate={s}
                      symbol={symbol}
                      chartHeight={OPTIONS_CALCULATOR_CHART_HEIGHT}
                      sectionGap={1.5}
                    />

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                      >
                        Risk profile
                      </Typography>
                      <KVTable
                        section="Risk profile"
                        onAsk={onAsk}
                        rows={[
                          {
                            label: 'Risk type',
                            value: isFixedRisk
                              ? 'Fixed risk'
                              : 'NOT fixed risk',
                          },
                          ...(isInternalUser
                            ? [
                                {
                                  label: 'Max profit / loss ratio',
                                  value: rewardRisk,
                                },
                              ]
                            : []),
                          {
                            label: 'OCC Risk Score (margin requirement)',
                            value:
                              s.risk_score != null
                                ? `${s.risk_score.total.toFixed(0)}/100`
                                : 'N/A',
                          },
                        ]}
                        valueColorFn={(label, value) =>
                          label === 'Risk type' && value === 'NOT fixed risk'
                            ? theme.palette.warning.main
                            : undefined
                        }
                      />
                    </Box>

                    {hasNotes && (
                      <Box>
                        {s.carry != null && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: theme.palette.text.primary,
                            }}
                          >
                            Carry: {usd(s.carry.theta_daily)}/d |{' '}
                            {usd(s.carry.theta_weekly)}/w | Total:{' '}
                            {usd(s.carry.total_carry)}
                          </Typography>
                        )}
                        {s.theta_income != null && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: theme.palette.success.main,
                            }}
                          >
                            Theta Income: {usd(s.theta_income.daily)}/d |{' '}
                            {usd(s.theta_income.total_to_expiry)} total
                          </Typography>
                        )}
                        {s.oi_annotation != null &&
                          ((s.oi_annotation.warning != null &&
                            s.oi_annotation.warning !== '') ||
                            (s.oi_annotation.confirming != null &&
                              s.oi_annotation.confirming !== '')) && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                fontWeight:
                                  s.oi_annotation.warning != null &&
                                  s.oi_annotation.warning !== ''
                                    ? 600
                                    : 400,
                                color:
                                  s.oi_annotation.warning != null &&
                                  s.oi_annotation.warning !== ''
                                    ? theme.palette.warning.main
                                    : theme.palette.info.main,
                              }}
                            >
                              {s.oi_annotation.warning != null &&
                              s.oi_annotation.warning !== ''
                                ? `⚠ OI: ${s.oi_annotation.warning}`
                                : `✓ OI: ${s.oi_annotation.confirming}`}
                            </Typography>
                          )}
                        {s.oi_annotation?.suggestion != null &&
                          s.oi_annotation.suggestion !== '' &&
                          s.oi_annotation.warning != null &&
                          s.oi_annotation.warning !== '' && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                color: theme.palette.text.secondary,
                                ml: 1,
                              }}
                            >
                              → {s.oi_annotation.suggestion}
                            </Typography>
                          )}
                      </Box>
                    )}

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                      >
                        Regime
                        {isInternalUser &&
                          `: ${regime.regime} ${pct0(regime.confidence)}`}
                      </Typography>
                      <KVTable
                        section="Regime"
                        onAsk={onAsk}
                        rows={[
                          { label: 'Overlay', value: enriched_regime.overlay },
                          { label: 'IV Level', value: regime.iv_level },
                          {
                            label: 'Term Struct',
                            value: regime.term_structure,
                          },
                          {
                            label: 'IV Rank',
                            value:
                              enriched_regime.iv_rank != null
                                ? `${num(enriched_regime.iv_rank, 0)}th pctl`
                                : 'N/A',
                          },
                          {
                            label: 'Event Flag',
                            value: regime.event_flag ? 'YES' : 'No',
                          },
                          ...(isInternalUser
                            ? [
                                {
                                  label: 'Surf Quality',
                                  value: num(
                                    volatility.surface_quality.composite,
                                  ),
                                },
                                {
                                  label: 'Blend',
                                  value: `${pct0(volatility.blend_weight)} pri / ${pct0(1 - volatility.blend_weight)} alt`,
                                },
                                {
                                  label: 'Spot-IV Corr',
                                  value: num(regime.spot_atm_corr, 2),
                                },
                                {
                                  label: 'Skew-IV Corr',
                                  value: num(regime.spot_skew_corr, 2),
                                },
                              ]
                            : []),
                          ...(isInternalUser && spot_vol_regime != null
                            ? [
                                {
                                  label: 'Spot Vol Reg',
                                  value: spot_vol_regime
                                    .replace(/([A-Z])/g, ' $1')
                                    .trim(),
                                },
                              ]
                            : []),
                        ]}
                      />
                    </Box>

                    {correlation_regime != null &&
                      correlation_regime.metrics.sector_etf !== 'UNKNOWN' && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                          >
                            Correlation Regime: {correlation_regime.regime}
                          </Typography>
                          <Stack sx={{ gap: '14px' }}>
                            <KVTable
                              section="Correlation Regime"
                              onAsk={onAsk}
                              rows={[
                                {
                                  label: 'Regime',
                                  value: correlation_regime.regime
                                    .replace(/([A-Z])/g, ' $1')
                                    .trim(),
                                },
                                {
                                  label: 'Confidence',
                                  value: pct0(correlation_regime.confidence),
                                },
                                {
                                  label: 'Vol Multiplier',
                                  value: `${num(correlation_regime.vol_multiplier, 2)}x`,
                                },
                                {
                                  label: 'MR Speed',
                                  value: `${num(correlation_regime.mr_speed_multiplier, 2)}x`,
                                },
                                {
                                  label: 'Reason',
                                  value:
                                    correlation_regime.detection_reason.split(
                                      ':',
                                    )[0],
                                },
                              ]}
                            />
                            <Box sx={{ px: 0.75, py: 0.25 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  display: 'block',
                                  mb: 0.5,
                                }}
                              >
                                Probabilities
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{ mb: 0.5 }}
                              >
                                {[
                                  {
                                    label: 'IDIO',
                                    value:
                                      correlation_regime.prob_idiosyncratic,
                                  },
                                  {
                                    label: 'SECTOR',
                                    value:
                                      correlation_regime.prob_sector_stress,
                                  },
                                  {
                                    label: 'SYS',
                                    value: correlation_regime.prob_systemic,
                                  },
                                ].map((p) => (
                                  <Box
                                    key={p.label}
                                    sx={{ flex: 1, textAlign: 'center' }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: theme.palette.text.primary,
                                        display: 'block',
                                      }}
                                    >
                                      {p.label}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                      }}
                                    >
                                      {pct0(p.value)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                            <MiniTable<CorrelationMetrics>
                              section="Correlation Metrics"
                              onAsk={onAsk}
                              headers={['Metric', 'Value']}
                              rawData={[correlation_regime.metrics]}
                              rows={[
                                [
                                  'Corr SPY 20d',
                                  num(
                                    correlation_regime.metrics.corr_spy_20d,
                                    2,
                                  ),
                                ],
                                [
                                  'Corr SPY 60d',
                                  num(
                                    correlation_regime.metrics.corr_spy_60d,
                                    2,
                                  ),
                                ],
                                [
                                  'Corr Sector 20d',
                                  num(
                                    correlation_regime.metrics.corr_sector_20d,
                                    2,
                                  ),
                                ],
                                [
                                  'Corr Sector 60d',
                                  num(
                                    correlation_regime.metrics.corr_sector_60d,
                                    2,
                                  ),
                                ],
                                [
                                  'Implied Corr',
                                  num(
                                    correlation_regime.metrics
                                      .implied_correlation,
                                    2,
                                  ),
                                ],
                                [
                                  'Sector ETF',
                                  correlation_regime.metrics.sector_etf,
                                ],
                              ]}
                            />
                          </Stack>
                        </Box>
                      )}

                    {vol_forecast != null &&
                      (vol_forecast.har_forecast_5d != null ||
                        vol_forecast.garch_forecast_10d != null ||
                        vol_forecast.ensemble_forecast != null) && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                          >
                            Vol Forecast
                          </Typography>
                          <KVTable
                            section="Vol Forecast"
                            onAsk={onAsk}
                            rows={[
                              ...(vol_forecast.har_forecast_5d != null
                                ? [
                                    {
                                      label: 'HAR 5d',
                                      value: pct(vol_forecast.har_forecast_5d),
                                    },
                                  ]
                                : []),
                              ...(vol_forecast.garch_forecast_10d != null
                                ? [
                                    {
                                      label: 'GARCH 10d',
                                      value: pct(
                                        vol_forecast.garch_forecast_10d,
                                      ),
                                    },
                                  ]
                                : []),
                              ...(vol_forecast.ensemble_forecast != null
                                ? [
                                    {
                                      label: 'Ensemble',
                                      value: pct(
                                        vol_forecast.ensemble_forecast
                                          .point_estimate,
                                      ),
                                    },
                                    {
                                      label: '95% CI',
                                      value: `${pct(vol_forecast.ensemble_forecast.lower_bound)} - ${pct(vol_forecast.ensemble_forecast.upper_bound)}`,
                                    },
                                  ]
                                : []),
                              ...(vol_forecast.har_r_squared != null
                                ? [
                                    {
                                      label: 'HAR R²',
                                      value: num(vol_forecast.har_r_squared, 2),
                                    },
                                  ]
                                : []),
                              ...(vol_forecast.garch_persistence != null
                                ? [
                                    {
                                      label: 'GARCH Persist',
                                      value: num(
                                        vol_forecast.garch_persistence,
                                        3,
                                      ),
                                    },
                                  ]
                                : []),
                            ]}
                          />
                        </Box>
                      )}

                    {synth_oi != null && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                        >
                          Synthetic OI
                        </Typography>
                        <KVTable
                          section="Synthetic OI"
                          onAsk={onAsk}
                          rows={[
                            ...(isInternalUser
                              ? [
                                  {
                                    label: 'Direction',
                                    value:
                                      synth_oi.entity_direction.toUpperCase(),
                                  },
                                ]
                              : []),
                            ...(synth_oi.iv_rank != null
                              ? [
                                  {
                                    label: 'IV Rank',
                                    value: `${Math.round(synth_oi.iv_rank * 100)}th`,
                                  },
                                ]
                              : []),
                            ...(synth_oi.skew_rank != null
                              ? [
                                  {
                                    label: 'Skew Rank',
                                    value: `${Math.round(synth_oi.skew_rank * 100)}th`,
                                  },
                                ]
                              : []),
                            ...(synth_oi.iv_pct != null
                              ? [
                                  {
                                    label: 'IV Pct',
                                    value: pct(synth_oi.iv_pct),
                                  },
                                ]
                              : []),
                            {
                              label: 'Spot',
                              value: usd(synth_oi.spot),
                            },
                          ]}
                        />
                        {synth_oi.key_levels.slice(0, 4).map((lv, i) => (
                          <Typography
                            key={i}
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: theme.palette.text.primary,
                            }}
                          >
                            {lv.level_type === 'resistance' ? '▲' : '▼'} $
                            {lv.price.toFixed(2)} {lv.entity} [{lv.strength}]
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Stack>
                </TabPanel>

                <TabPanel value="volatility" sx={{ p: 0 }}>
                  <Stack sx={{ gap: '16px' }}>
                    {vol_surface_3d != null &&
                      vol_surface_3d.expirations.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              display: 'block',
                              mb: 0.5,
                            }}
                          >
                            Volatility Surface
                          </Typography>
                          <VolatilityChartPanel
                            surfaceData={
                              vol_surface_3d as unknown as VolatilitySurface3DType
                            }
                            projectedSurface={projected_surface}
                            isFullScreen={isFullScreen}
                          />
                        </Box>
                      )}

                    {skew_plot_data != null &&
                      skew_plot_data.expirations.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              display: 'block',
                              mb: 0.5,
                            }}
                          >
                            Volatility Skew
                          </Typography>
                          <StrategyVolSkewChart
                            searchCandidate={s}
                            symbol={symbol}
                            spot={spot}
                            skewData={skew_plot_data}
                            dollarValueContracts={dollar_value_contracts}
                          />
                        </Box>
                      )}

                    {volatility.expirations.length > 0 && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                        >
                          Term Structure
                        </Typography>
                        <StrategyTermStructureChart
                          searchCandidate={s}
                          symbol={symbol}
                          volatility={volatility}
                          projectedSurface={projected_surface}
                        />
                      </Box>
                    )}

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                      >
                        Forecast (1d / 5d)
                      </Typography>
                      <KVTable
                        section="Forecast"
                        onAsk={onAsk}
                        rows={[
                          {
                            label: 'ATM IV 30d',
                            value: pct(forecast.current_atm),
                          },
                          ...(isInternalUser
                            ? [
                                {
                                  label: 'Forecast 1d',
                                  value: pct(forecast.forecast_1d),
                                },
                              ]
                            : []),
                          {
                            label: 'Range',
                            value: `${pct(forecast.forecast_low)} - ${pct(forecast.forecast_high)}`,
                          },
                          ...(isInternalUser
                            ? [
                                {
                                  label: 'Direction',
                                  value: `${forecast.direction} (${pct0(forecast.confidence)})`,
                                },
                                { label: 'Model', value: forecast.model },
                              ]
                            : []),
                        ]}
                      />
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                      >
                        Volatility Snapshot
                      </Typography>
                      <KVTable
                        section={`${s.family_label} Volatility`}
                        onAsk={onAsk}
                        rows={[
                          { label: 'Spot', value: usd(spot) },
                          ...(isInternalUser
                            ? [
                                {
                                  label: 'Forward (30d)',
                                  value: usd(volatility.forward_30d),
                                },
                              ]
                            : []),
                          {
                            label: 'ATM IV (7d)',
                            value: pct(volatility.atm_iv_7d),
                          },
                          {
                            label: 'ATM IV (30d)',
                            value: pct(volatility.atm_iv_30d),
                          },
                          {
                            label: 'ATM IV (90d)',
                            value: pct(volatility.atm_iv_90d),
                          },
                          {
                            label: 'RV (c2c)',
                            value: `${pct(volatility.realized_vol.estimate)} [${volatility.realized_vol.method}]`,
                          },
                          {
                            label: 'RV (5d)',
                            value: pct(volatility.realized_vol.rv_5d),
                          },
                          {
                            label: 'RV (20d)',
                            value: pct(volatility.realized_vol.rv_20d),
                          },
                          ...(isInternalUser
                            ? [
                                {
                                  label: 'Trend',
                                  value: volatility.realized_vol.trend,
                                },
                              ]
                            : []),
                          ...(isInternalUser && volatility.forward_vol != null
                            ? [
                                {
                                  label: 'Fwd Vol',
                                  value: `${pct(volatility.forward_vol.forward_iv)} ${volatility.forward_vol.term_slope}`,
                                },
                              ]
                            : []),
                          ...(isInternalUser &&
                          volatility.vt_forward_vol != null
                            ? [
                                {
                                  label: 'VT-adj',
                                  value: pct(volatility.vt_forward_vol),
                                },
                              ]
                            : []),
                          {
                            label: '25d Skew',
                            value: `+${pct(volatility.skew_25d_30d)}`,
                          },
                          {
                            label: 'N Days',
                            value: `${volatility.realized_vol.n_spots}`,
                          },
                        ]}
                      />
                    </Box>

                    {volatility.term_structure != null && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                        >
                          Term Structure
                          {volatility.term_structure.event_detected
                            ? ' [EVENT]'
                            : ''}
                        </Typography>
                        <Stack sx={{ gap: '14px' }}>
                          {volatility.term_structure.forward_vols.length >
                            0 && (
                            <MiniTable<ForwardVolPair>
                              section="Term Structure"
                              onAsk={onAsk}
                              headers={[
                                'Short',
                                'Long',
                                ...(isInternalUser ? ['Dirty Fwd'] : []),
                                'Clean Fwd',
                                'Valid',
                              ]}
                              rawData={volatility.term_structure.forward_vols}
                              rows={volatility.term_structure.forward_vols.map(
                                (fv) => [
                                  `${fv.short_dte}d`,
                                  `${fv.long_dte}d`,
                                  ...(isInternalUser
                                    ? [pct(fv.forward_vol_dirty)]
                                    : []),
                                  pct(fv.forward_vol_clean),
                                  fv.is_valid ? 'Yes' : 'No',
                                ],
                              )}
                            />
                          )}
                          {isInternalUser &&
                            volatility.all_forward_vols.length > 0 && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    display: 'block',
                                    mb: 0.5,
                                  }}
                                >
                                  Forward Vol Table
                                </Typography>
                                <MiniTable
                                  section="Forward Vol Table"
                                  onAsk={onAsk}
                                  headers={[
                                    'Short DTE',
                                    'Long DTE',
                                    'Forward Vol',
                                  ]}
                                  rows={volatility.all_forward_vols.map(
                                    (fv) => [
                                      `${fv[0]}d`,
                                      `${fv[1]}d`,
                                      pct(fv[2]),
                                    ],
                                  )}
                                />
                              </Box>
                            )}
                        </Stack>
                      </Box>
                    )}

                    {volatility.event_signal != null && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                        >
                          Event Signal
                          {volatility.event_signal.detected
                            ? ' [DETECTED]'
                            : ''}
                        </Typography>
                        <KVTable
                          section="Event Signal"
                          onAsk={onAsk}
                          rows={[
                            {
                              label: 'Detected',
                              value: volatility.event_signal.detected
                                ? 'YES'
                                : 'No',
                            },
                            ...(volatility.event_signal.detected
                              ? [
                                  {
                                    label: 'Source',
                                    value: volatility.event_signal.event_source,
                                  },
                                  {
                                    label: 'Confidence',
                                    value: pct0(
                                      volatility.event_signal.confidence,
                                    ),
                                  },
                                  ...(volatility.event_signal.days_to_event !=
                                  null
                                    ? [
                                        {
                                          label: 'Days to Event',
                                          value: `${volatility.event_signal.days_to_event}`,
                                        },
                                      ]
                                    : []),
                                  {
                                    label: 'Fwd Vol Ratio',
                                    value: num(
                                      volatility.event_signal.forward_vol_ratio,
                                    ),
                                  },
                                  ...(volatility.event_signal.is_post_event
                                    ? [
                                        {
                                          label: 'Post-Event',
                                          value: 'YES',
                                        },
                                      ]
                                    : []),
                                ]
                              : []),
                          ]}
                          valueColorFn={(label) => {
                            if (
                              label === 'Detected' &&
                              volatility.event_signal?.detected === true
                            ) {
                              return theme.palette.warning.main;
                            }
                            return undefined;
                          }}
                        />
                      </Box>
                    )}

                    {isInternalUser && volatility.carry_quality != null && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                        >
                          Carry Quality
                        </Typography>
                        <KVTable
                          section="Carry Quality"
                          onAsk={onAsk}
                          rows={[
                            {
                              label: 'Bergomi Sharpe',
                              value: num(volatility.carry_quality.carry_sharpe),
                            },
                            {
                              label: 'Conviction',
                              value: volatility.carry_quality.quality,
                            },
                          ]}
                          valueColorFn={(label, value) => {
                            if (label === 'Conviction') {
                              return getCarryColor(value, theme);
                            }
                            return undefined;
                          }}
                        />
                      </Box>
                    )}

                    {isInternalUser && scenario_forecast != null && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                        >
                          Scenario Forecast
                        </Typography>
                        {scenario_forecast.scenarios.length === 0 ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.primary,
                              fontStyle: 'italic',
                            }}
                          >
                            No scenario analysis available for this symbol yet
                          </Typography>
                        ) : (
                          <Stack sx={{ gap: '14px' }}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                }}
                              >
                                IV Range: {pct(scenario_forecast.iv_range_low)}{' '}
                                - {pct(scenario_forecast.iv_range_high)} | Bias:{' '}
                                {scenario_forecast.directional_bias}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  fontStyle: 'italic',
                                  color: theme.palette.text.primary,
                                }}
                              >
                                {scenario_forecast.key_insight}
                              </Typography>
                            </Box>
                            <MiniTable
                              section="Scenario Forecast"
                              onAsk={onAsk}
                              headers={[
                                'Scenario',
                                'Spot Move',
                                'Final IV',
                                'Regime',
                                'Notes',
                              ]}
                              rows={scenario_forecast.scenarios.map((sc) => [
                                sc.name,
                                `${sc.spot_change > 0 ? '+' : ''}${pct(sc.spot_change)}`,
                                pct(sc.final_atm_iv),
                                sc.regime_likely,
                                sc.notes,
                              ])}
                            />
                          </Stack>
                        )}
                      </Box>
                    )}

                    {isInternalUser && vrp_term_structure != null && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                        >
                          VRP Term Structure
                        </Typography>
                        <Stack sx={{ gap: '14px' }}>
                          <KVTable
                            section="VRP Term Structure"
                            onAsk={onAsk}
                            rows={[
                              {
                                label: 'Richest Tenor',
                                value: `${vrp_term_structure.richest_dte}d`,
                              },
                              {
                                label: 'Cheapest Tenor',
                                value: `${vrp_term_structure.cheapest_dte}d`,
                              },
                            ]}
                            valueColorFn={(label) => {
                              if (label === 'Richest Tenor') {
                                return theme.palette.error.main;
                              }
                              if (label === 'Cheapest Tenor') {
                                return theme.palette.success.main;
                              }
                              return undefined;
                            }}
                          />
                          {vrp_term_structure.tenors.length > 0 && (
                            <MiniTable
                              section="VRP Term Structure"
                              onAsk={onAsk}
                              headers={['DTE', 'IV', 'RV', 'Ratio', 'VRP']}
                              rawData={vrp_term_structure.tenors}
                              rows={vrp_term_structure.tenors.map((t) => [
                                `${t.dte}d`,
                                pct(t.iv),
                                pct(t.rv),
                                num(t.ratio),
                                `${t.level > 0 ? '+' : ''}${pct(t.level)}`,
                              ])}
                              columnColorFns={[
                                () => undefined,
                                () => undefined,
                                () => undefined,
                                (_v, _r, _i, raw) =>
                                  raw != null
                                    ? getVRPColor(raw.ratio - 1, theme)
                                    : undefined,
                                (_v, _r, _i, raw) =>
                                  raw != null
                                    ? getVRPColor(raw.level, theme)
                                    : undefined,
                              ]}
                            />
                          )}
                        </Stack>
                      </Box>
                    )}

                    {isInternalUser && projected_surface != null && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                        >
                          Projected Surface ({projected_surface.method})
                        </Typography>
                        <Stack sx={{ gap: '14px' }}>
                          <KVTable
                            section="Projected Surface"
                            onAsk={onAsk}
                            rows={[
                              {
                                label: 'Projected ATM IV',
                                value: pct(projected_surface.projected_atm_iv),
                              },
                            ]}
                          />
                          {projected_surface.projected_expirations.length >
                            0 && (
                            <MiniTable
                              section="Projected Surface"
                              onAsk={onAsk}
                              headers={['DTE', 'ATM IV', '25d Skew']}
                              rows={projected_surface.projected_expirations.map(
                                (e) => [
                                  `${e.dte}d`,
                                  pct(e.atm_iv),
                                  `+${pct(e.skew_25d)}`,
                                ],
                              )}
                            />
                          )}
                        </Stack>
                      </Box>
                    )}

                    {isInternalUser && surface_residuals != null && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                        >
                          Surface Residuals
                        </Typography>
                        <Stack sx={{ gap: '14px' }}>
                          <KVTable
                            section="Surface Residuals"
                            onAsk={onAsk}
                            rows={[
                              {
                                label: 'RMSE',
                                value: num(surface_residuals.rmse, 4),
                              },
                              {
                                label: 'Wing Skew Richness',
                                value: num(
                                  surface_residuals.wing_skew_richness,
                                  3,
                                ),
                              },
                            ]}
                            valueColorFn={(label) => {
                              if (label === 'Wing Skew Richness') {
                                return getVRPColor(
                                  surface_residuals?.wing_skew_richness ?? 0,
                                  theme,
                                );
                              }
                              return undefined;
                            }}
                          />
                          {surface_residuals.points.length > 0 && (
                            <MiniTable
                              section="Surface Residuals"
                              onAsk={onAsk}
                              headers={['Label', 'DTE', 'Residual', 'Signal']}
                              rawData={surface_residuals.points}
                              rows={surface_residuals.points.map((p) => [
                                p.label,
                                `${p.dte}d`,
                                num(p.residual, 4),
                                p.signal,
                              ])}
                              columnColorFns={[
                                () => undefined,
                                () => undefined,
                                (_v, _r, _i, raw) =>
                                  raw != null
                                    ? getVRPColor(raw.residual, theme)
                                    : undefined,
                                (v) => {
                                  if (v === 'Rich') {
                                    return theme.palette.error.main;
                                  }
                                  if (v === 'Cheap') {
                                    return theme.palette.success.main;
                                  }
                                  return theme.palette.text.primary;
                                },
                              ]}
                            />
                          )}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </TabPanel>
              </Box>
            </Box>
          </TabContext>
        </CardContent>
      </Card>
    </ExpandableContentWrapper>
  );
};

export default MoviefoneStrategyCard;
